import { HttpError, HttpRemoteData, HttpResult, HttpStatusCode, HttpTask } from '@core/http/model';
import * as RD from 'fp-ts-remote-data';
import * as EI from 'fp-ts/Either';
import { Lazy, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import * as T from 'fp-ts/Task';
import isEqual from 'lodash.isequal';
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState, useTransition } from 'react';
import { showNotification } from '@shared/utils/notification';

function useCurrentArgs<P extends unknown[]>(...args: P) {
  const [currentArgs, setCurrentArgs] = useState(args);

  useEffect(() => {
    setCurrentArgs(old => (!isEqual(old, args) ? args : old));
  }, [args]);

  return currentArgs;
}

export function useFetchTask<P extends unknown[], R, E>(
  task: (...args: P) => HttpTask<R, E>,
  ...args: P
): [HttpRemoteData<R, E>, HttpTask<R, E>, boolean, Dispatch<SetStateAction<HttpRemoteData<R, E>>>] {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<HttpRemoteData<R, E>>(RD.pending);

  const currentArgs = useCurrentArgs(...args);

  const fetchData = useCallback(() => {
    setLoading(true);

    return pipe(
      task(...currentArgs),
      T.chainIOK(res => () => {
        setLoading(false);
        setData(RD.fromEither(res));

        return res;
      }),
    )();
  }, [currentArgs, task]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return [data, fetchData, loading, setData];
}

export interface UseSendTaskParams {
  hideError?: true | Array<HttpStatusCode>;
  successMessage?: ReactNode;
}

export function useSendTask<P1 extends unknown[], P2 extends unknown[], R, E>(
  task: (...args: [...P1, ...P2]) => HttpTask<R, E>,
  { hideError, successMessage }: UseSendTaskParams = {},
  ...args: P1
): [boolean, (...args: P2) => Promise<HttpResult<R, E>>, O.Option<HttpError<E>>, boolean, Lazy<void>] {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<O.Option<HttpError<E>>>(O.none);
  const [success, setSuccess] = useState<boolean>(false);

  const currentArgs = useCurrentArgs(...args);

  const [, startTransition] = useTransition();

  const sendRequest = useCallback(
    (...args2: P2) => {
      setLoading(true);

      setSuccess(false);

      startTransition(() => {
        setError(O.none);
      });

      return pipe(
        task(...[...currentArgs, ...args2]),
        T.chainIOK(res => () => {
          setLoading(false);

          startTransition(() => {
            setSuccess(EI.isRight(res));
            setError(O.fromEither(EI.swap(res)));
          });

          if (EI.isRight(res) && successMessage) {
            showNotification({ message: successMessage });
          } else if (EI.isLeft(res)) {
            const show = pipe(
              O.fromNullable(hideError),
              O.fold(
                () => true,
                hide => (Array.isArray(hide) ? !hide.includes(res.left.status) : false),
              ),
            );

            if (show) {
              showNotification({ message: res.left.message });
            }
          }

          return res;
        }),
      )();
    },
    [task, currentArgs, hideError, successMessage],
  );

  const reset = () => {
    setError(O.none);
    setSuccess(false);
  };

  return [loading, sendRequest, error, success, reset];
}
